import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output,
  SimpleChanges, ViewChild
} from '@angular/core';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import { buildSchedulePicks, ChatQuickPick, ChatTemplateVars, combineDateTime, formatWhen, resolveTemplateVars, toDateInput, toTimeInput } from '../../chat.format';
import { ChatComposerAction, ChatComposerSchedule, ChatComposerSubmit, ChatMention, ChatMessage, ChatQuickReply, ChatScheduledMessage } from '../../chat.model';
import { CHAT_EMOJI_LABELS_PT, ChatEmojiPickerLabels } from '../../chat.emojis';

/** What the file input accepts: the types the server keeps (contract 5). */
export const CHAT_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,application/pdf';
/** The "Enviar imagem" button: images only. */
export const CHAT_ACCEPT_IMAGES = 'image/jpeg,image/png,image/gif,image/webp';

/** `typing { typing: false }` is sent after this long without a key (contract 6.3). */
const TYPING_IDLE_MS = 3000;
/** Server limit of a text message; the counter appears in the last 200 characters. */
const MAX_LENGTH = 2000;
const COUNTER_FROM = 200;
/** Textarea grows up to 6 lines of 20px (+ 20px vertical padding). */
const LINE_HEIGHT = 20;
const MAX_ROWS = 6;
const FIELD_PADDING = 20;
/** How long the send button shows the "a enviar" spinner after a submit. */
const SEND_PULSE_MS = 700;

/** One file picked and waiting in the composer. */
export interface ChatComposerAttachment {
  file: File;
  /** Data URL for images, '' for PDFs. */
  preview: string;
  image: boolean;
}

/**
 * The composer of the reference layout: a toolbar of 32px icon buttons
 * (emoji, attach, property, image, quick replies, "+" actions, | internal
 * note) with the emoji picker anchored to its button, and a white rounded box
 * with an auto-growing textarea (Enter sends, Shift+Enter breaks the line, up
 * to 6 lines) and the square primary send button.
 *
 * `conversationKey` identifies the open conversation: when it changes the
 * draft, the attachments, the mentions and the note mode are reset, so
 * nothing written for one conversation leaks into the next.
 *
 * Secção 9 (como no WhatsApp): com `editing` o campo recebe o texto da
 * mensagem, aparece a faixa "A editar mensagem" e o envio emite `editSubmit`
 * em vez de `send`; Escape ou o X emitem `editCancel`. Anexos e ações ficam
 * desligados enquanto se edita.
 *
 * Attachments picked are previewed as thumbnails (with remove) and only leave
 * with the send button: `files` emits the File[] (the container validates the
 * server limits and uploads), `send` emits the text. Nothing is decided here
 * about capabilities: a control is shown when its flag is true and emits.
 *
 * Quick replies: `/` at the start of the field opens the list filtered by the
 * prefix; Enter / click replaces the field. The bolt button opens the same
 * list unfiltered.
 *
 * Typing: `typing` emits true on the first key of a burst and false after
 * 3 s without keys (or on send). Only in "Responder".
 */
@Component({
  selector: 'chat-composer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-composer.component.html',
  styleUrls: ['./chat-composer.component.css']
})
export class ChatComposerComponent implements OnChanges, OnDestroy {
  @Input() canWriteNotes = false;
  @Input() canSuggestProperties = false;
  /** Own + general quick replies, already filtered to the active ones. */
  @Input() quickReplies: ChatQuickReply[] = [];
  /** Valores das {{variáveis}} das respostas rápidas para a conversa aberta (resolveTemplateVars). */
  @Input() templateVars: ChatTemplateVars | null = null;
  /** Colegas que se podem mencionar com `@` numa nota (7.1): staff da entidade. */
  @Input() mentionCandidates: ChatMention[] = [];
  /** Ações da conversa sem ícone próprio (agendar visita, avaliação, qualificar, proposta): menu "+". */
  @Input() actions: ChatComposerAction[] = [];
  /** A conversa aberta (`chat:<id>` / `user:<id>`): mudar repõe o compositor. */
  @Input() conversationKey: string | null = null;
  /** Secção 9: a mensagem a editar (o contentor já verificou a janela de 15 min). */
  @Input() editing: ChatMessage | null = null;
  /** Secção 10: a mensagem a que se responde, e o nome que a faixa mostra ("Você" se for minha). */
  @Input() replyingTo: ChatMessage | null = null;
  @Input() replyName = '';
  /** Secção 12: "enviar mais tarde" (só staff, só texto) e as mensagens já agendadas nesta conversa. */
  @Input() canSchedule = false;
  @Input() scheduled: ChatScheduledMessage[] = [];
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;
  @Input() maxLength = MAX_LENGTH;

  @Output() send = new EventEmitter<ChatComposerSubmit>();
  @Output() files = new EventEmitter<File[]>();
  @Output() suggestProperty = new EventEmitter<void>();
  @Output() typing = new EventEmitter<boolean>();
  @Output() action = new EventEmitter<string>();
  @Output() editSubmit = new EventEmitter<{ messageId: string; text: string }>();
  @Output() editCancel = new EventEmitter<void>();
  @Output() replyCancel = new EventEmitter<void>();
  /** Secção 12: agendar o texto atual, e cancelar uma agendada. */
  @Output() schedule = new EventEmitter<ChatComposerSchedule>();
  @Output() cancelScheduled = new EventEmitter<string>();
  /** Mensagem de voz gravada (ficheiro + duração em segundos). */
  @Output() voice = new EventEmitter<{ file: File; duration: number }>();
  /** A gravar (true) / parou (false): o contentor avisa o outro lado, como o "a escrever…". */
  @Output() recordingChange = new EventEmitter<boolean>();

  /* mensagem de voz: gravação com o MediaRecorder do browser */
  recording = false;
  recordSeconds = 0;
  recordError = '';
  private recorder: MediaRecorder | null = null;
  private recordChunks: Blob[] = [];
  private recordTimer: any = null;
  private recordStartedAt = 0;
  private recordStream: MediaStream | null = null;
  private recordSend = false;
  /** 3 minutos chegam para uma mensagem de voz; a 32 kbps são uns 700 KB em qualquer browser. */
  static readonly RECORD_MAX_SECONDS = 180;
  static readonly RECORD_BITS_PER_SECOND = 32000;

  /** O microfone aparece sem texto nem anexos, fora da edição e das notas, quando o browser grava. */
  get canRecord(): boolean {
    return typeof MediaRecorder !== 'undefined' && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      && !this.hasText && this.attachments.length === 0 && !this.editing && !this.isNote && !this.replyingTo;
  }

  get recordTime(): string {
    const m = Math.floor(this.recordSeconds / 60);
    const s = this.recordSeconds % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  async startRecording(): Promise<void> {
    if (this.recording) {
      return;
    }
    this.recordError = '';
    try {
      this.recordStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      this.recordError = this.strings.micDenied;
      this.cdr.markForCheck();
      return;
    }
    const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
      .find(t => (MediaRecorder as any).isTypeSupported && MediaRecorder.isTypeSupported(t));
    try {
      // Taxa baixa de propósito: voz, não música. O Chrome respeita; o Safari aproxima-se.
      const options: MediaRecorderOptions = { audioBitsPerSecond: ChatComposerComponent.RECORD_BITS_PER_SECOND, ...(mimeType ? { mimeType } : {}) };
      this.recorder = new MediaRecorder(this.recordStream, options);
    } catch {
      this.releaseMic();
      this.recordError = this.strings.micDenied;
      this.cdr.markForCheck();
      return;
    }
    this.recordChunks = [];
    this.recordSend = false;
    this.recorder.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) {
        this.recordChunks.push(e.data);
      }
    };
    this.recorder.onstop = () => this.onRecorderStopped();
    this.recorder.start(250);
    this.recording = true;
    this.recordSeconds = 0;
    this.recordStartedAt = Date.now();
    this.stopTyping(false);
    this.recordingChange.emit(true);
    this.recordTimer = setInterval(() => {
      this.recordSeconds = Math.floor((Date.now() - this.recordStartedAt) / 1000);
      if (this.recordSeconds >= ChatComposerComponent.RECORD_MAX_SECONDS) {
        this.stopRecording(true);
      }
      this.cdr.markForCheck();
    }, 500);
    this.cdr.markForCheck();
  }

  /** Parar: com `send` o áudio segue como mensagem; sem ele deita-se fora. */
  stopRecording(send: boolean): void {
    if (!this.recording || !this.recorder) {
      return;
    }
    this.recordSend = send;
    this.recording = false;
    this.recordingChange.emit(false);
    if (this.recordTimer) {
      clearInterval(this.recordTimer);
      this.recordTimer = null;
    }
    if (this.recorder.state !== 'inactive') {
      this.recorder.stop();
    } else {
      this.onRecorderStopped();
    }
  }

  private onRecorderStopped(): void {
    const type = (this.recorder && this.recorder.mimeType) || 'audio/webm';
    const duration = Math.max(1, Math.round((Date.now() - this.recordStartedAt) / 1000));
    const chunks = this.recordChunks;
    this.recorder = null;
    this.recordChunks = [];
    this.releaseMic();
    if (this.recordSend && chunks.length > 0 && duration >= 1) {
      const ext = type.indexOf('mp4') > -1 ? 'm4a' : type.indexOf('ogg') > -1 ? 'ogg' : 'webm';
      const blob = new Blob(chunks, { type: type.split(';')[0] });
      const file = new File([blob], 'voz-' + Date.now() + '.' + ext, { type: blob.type });
      this.voice.emit({ file, duration: Math.min(duration, ChatComposerComponent.RECORD_MAX_SECONDS) });
    }
    this.recordSend = false;
    this.cdr.markForCheck();
  }

  private releaseMic(): void {
    if (this.recordStream) {
      this.recordStream.getTracks().forEach(t => t.stop());
      this.recordStream = null;
    }
  }

  /* secção 12: o painel "enviar quando?" */
  scheduleOpen = false;
  schedulePicks: ChatQuickPick[] = [];
  scheduleDate = '';
  scheduleTime = '';

  /** O botão do relógio: com texto, sem anexos, fora da edição. */
  get canScheduleNow(): boolean {
    return this.canSchedule && this.hasText && !this.editing && this.attachments.length === 0;
  }

  toggleSchedule(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.scheduleOpen) {
      const now = new Date();
      this.schedulePicks = buildSchedulePicks(now, this.strings);
      const first = this.schedulePicks[0];
      this.scheduleDate = first ? first.date : toDateInput(now);
      this.scheduleTime = first ? first.time : toTimeInput(now);
      this.closeQuick();
      this.closeMention();
      this.closeEmoji();
      this.actionsOpen = false;
    }
    this.scheduleOpen = !this.scheduleOpen;
  }

  pickSchedule(p: ChatQuickPick): void {
    this.submitSchedule(p.at);
  }

  submitScheduleCustom(): void {
    const when = combineDateTime(this.scheduleDate, this.scheduleTime);
    if (when) {
      this.submitSchedule(when);
    }
  }

  /** Agenda o texto do campo (o campo esvazia, como num envio) e fecha o painel. */
  private submitSchedule(when: Date): void {
    const el = this.input.nativeElement;
    const text = (el.value || '').trim();
    if (!text || when.getTime() <= Date.now()) {
      return;
    }
    this.scheduleOpen = false;
    this.stopTyping(true);
    this.schedule.emit({ text, mode: this.isNote ? 'note' : 'reply', sendAt: when });
    this.mentions = [];
    el.value = '';
    this.hasText = false;
    this.remaining = this.maxLength;
    this.autogrow();
    this.pulse();
    el.focus();
  }

  scheduledWhen(item: ChatScheduledMessage): string {
    return this.strings.scheduledAt.replace('{when}', formatWhen(item.sendAt));
  }

  get scheduledTitle(): string {
    return this.scheduled.length === 1 ? this.strings.scheduledOne : this.strings.scheduledCount.replace('{n}', String(this.scheduled.length));
  }

  trackByScheduled = (_i: number, s: ChatScheduledMessage) => s.id;

  @HostListener('document:click')
  closeScheduleOnOutside(): void {
    if (this.scheduleOpen) {
      this.scheduleOpen = false;
      this.cdr.markForCheck();
    }
  }

  @ViewChild('input', { static: true }) input!: ElementRef<HTMLTextAreaElement>;

  mode: 'reply' | 'note' = 'reply';
  accept = CHAT_ACCEPT;
  acceptImages = CHAT_ACCEPT_IMAGES;
  quickOpen = false;
  quickFiltered: ChatQuickReply[] = [];
  quickIndex = 0;
  /* menções (7.1): `@nome` numa nota abre a lista de colegas */
  mentionOpen = false;
  mentionFiltered: ChatMention[] = [];
  mentionIndex = 0;
  private mentionStart = -1;
  private mentions: ChatMention[] = [];
  emojiOpen = false;
  actionsOpen = false;
  emojiLabels: ChatEmojiPickerLabels = CHAT_EMOJI_LABELS_PT;
  attachments: ChatComposerAttachment[] = [];
  hasText = false;
  remaining = MAX_LENGTH;
  counterFrom = COUNTER_FROM;
  sendingPulse = false;

  private quickPrefix = '';
  private quickManual = false;
  private typingActive = false;
  private typingTimer: any = null;
  private pulseTimer: any = null;

  constructor(private host: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) {}

  get placeholder(): string {
    return this.mode === 'note' ? this.strings.placeholderNote : this.strings.placeholderMessage;
  }

  get isNote(): boolean {
    return this.mode === 'note' && this.canWriteNotes;
  }

  /** A gravar, o botão envia a gravação (2026-10-28: antes ficava desativado, porque a gravação só começa com o campo vazio). */
  get canSend(): boolean {
    return this.recording || this.hasText || this.attachments.length > 0;
  }

  get attachmentsLabel(): string {
    const n = this.attachments.length;
    return n === 1 ? this.strings.attachmentSelectedOne : this.strings.attachmentsSelected.replace('{n}', String(n));
  }

  get counterLabel(): string {
    return this.strings.charactersLeft.replace('{n}', String(this.remaining));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['conversationKey'] && !changes['conversationKey'].firstChange) {
      this.reset();
    }
    if (changes['quickReplies'] && this.quickOpen) {
      this.filterQuick(this.quickPrefix);
    }
    if (changes['editing']) {
      this.applyEditing(!!changes['editing'].previousValue);
    }
    if (changes['replyingTo'] && this.replyingTo && this.input) {
      // Responder a uma nota só pode ser outra nota (o cliente nunca vê notas).
      if (this.replyingTo.kind === 'note' && this.canWriteNotes) {
        this.mode = 'note';
      }
      this.input.nativeElement.focus();
      this.cdr.markForCheck();
    }
    if (changes['strings']) {
      const s = this.strings;
      this.emojiLabels = {
        title: s.emoji, search: s.emojiSearch, empty: s.emojiEmpty, recent: s.emojiRecent, close: s.close, categories: s.emojiCategories
      };
    }
    if (changes['maxLength']) {
      this.remaining = this.maxLength - (this.input ? (this.input.nativeElement.value || '').length : 0);
    }
    if (changes['canWriteNotes'] && !this.canWriteNotes) {
      this.mode = 'reply';
    }
  }

  ngOnDestroy(): void {
    this.stopTyping(false);
    // Sair da página a gravar: deita fora e larga o microfone.
    this.stopRecording(false);
    this.releaseMic();
    if (this.pulseTimer) {
      clearTimeout(this.pulseTimer);
    }
  }

  /**
   * Outra conversa: o rascunho, os anexos, as menções, as listas abertas e o
   * modo nota eram da anterior. O `typing: false` da anterior é o contentor
   * que o envia (já sabe qual era); aqui só se pára o relógio.
   */
  private reset(): void {
    this.stopTyping(false);
    // Trocar de conversa a gravar: a gravação era da anterior (sem isto seguia para a nova).
    this.stopRecording(false);
    this.recordError = '';
    this.closeQuick();
    this.closeMention();
    this.closeEmoji();
    this.actionsOpen = false;
    this.mentions = [];
    this.attachments = [];
    this.mode = 'reply';
    if (this.input) {
      this.input.nativeElement.value = '';
      this.autogrow();
    }
    this.hasText = false;
    this.remaining = this.maxLength;
    this.cdr.markForCheck();
  }

  /** O texto da faixa "A responder a …": o da mensagem, ou o nome do cartão / anexo. */
  get replySnippet(): string {
    const m = this.replyingTo;
    if (!m) {
      return '';
    }
    if (m.message) {
      return m.message;
    }
    const kind = m.kind || 'text';
    if (kind !== 'text' && this.strings.previewByKind[kind]) {
      return this.strings.previewByKind[kind];
    }
    const n = (m.files || []).length;
    return n > 1 ? this.strings.attachmentsPreview.replace('{n}', String(n)) : this.strings.attachmentPreview;
  }

  /** Entrar (ou sair) do modo edição: o campo passa a ter o texto da mensagem, ou fica vazio. */
  private applyEditing(wasEditing: boolean): void {
    const m = this.editing;
    if (!this.input) {
      return;
    }
    const el = this.input.nativeElement;
    if (m) {
      this.stopTyping(true);
      this.closeQuick();
      this.closeMention();
      this.closeEmoji();
      this.actionsOpen = false;
      this.attachments = [];
      this.mode = m.kind === 'note' && this.canWriteNotes ? 'note' : 'reply';
      el.value = m.message || '';
      el.focus();
      const end = el.value.length;
      try {
        el.setSelectionRange(end, end);
      } catch (e) {
        /* alguns navegadores móveis não suportam seleção programática */
      }
    } else if (wasEditing) {
      el.value = '';
      this.mode = 'reply';
    }
    this.hasText = (el.value || '').trim().length > 0;
    this.remaining = Math.max(0, this.maxLength - (el.value || '').length);
    this.autogrow();
    this.cdr.markForCheck();
  }

  /* ---- mode ---- */

  setMode(mode: 'reply' | 'note'): void {
    if (mode === 'note' && !this.canWriteNotes) {
      mode = 'reply';
    }
    if (mode === 'note' && this.typingActive) {
      this.stopTyping(true);
    }
    if (mode === 'reply' && this.replyingTo && this.replyingTo.kind === 'note') {
      // Sair do modo nota larga a resposta a uma nota (não pode ir para o cliente).
      this.replyCancel.emit();
    }
    this.mode = mode;
    this.cdr.markForCheck();
    this.input.nativeElement.focus();
  }

  toggleNote(): void {
    this.setMode(this.isNote ? 'reply' : 'note');
  }

  /* ---- send ---- */

  submit(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    // A gravar: o botão de enviar é o mesmo de sempre e envia a gravação.
    if (this.recording) {
      this.stopRecording(true);
      return;
    }
    const el = this.input.nativeElement;
    const text = (el.value || '').trim();
    if (this.editing) {
      // Secção 9: o envio é a edição. Vazio não apaga (como no WhatsApp); igual cancela.
      if (!text) {
        return;
      }
      const editing = this.editing;
      if (text === (editing.message || '').trim() || !editing.messageId) {
        this.editCancel.emit();
      } else {
        this.editSubmit.emit({ messageId: editing.messageId, text });
      }
      return;
    }
    const picked = this.attachments.map(a => a.file);
    if (!text && picked.length === 0) {
      return;
    }
    // A note can only be written with the capability; the tab is not shown
    // without it, but a stale mode must not leak into a plain send.
    const mode = this.isNote ? 'note' : 'reply';
    this.closeQuick();
    this.closeMention();
    this.closeEmoji();
    this.stopTyping(true);
    if (text) {
      // Só as menções cujo `@Nome` ainda está no texto.
      const mentions = mode === 'note'
        ? this.mentions.filter(m => !!m.name && text.indexOf('@' + m.name) > -1)
        : [];
      const replyTo = this.replyingTo && this.replyingTo.messageId ? this.replyingTo.messageId : undefined;
      this.send.emit({ text, mode, mentions: mentions.length > 0 ? mentions : undefined, replyTo });
    }
    this.mentions = [];
    if (picked.length > 0) {
      this.files.emit(picked);
    }
    el.value = '';
    this.attachments = [];
    this.hasText = false;
    this.remaining = this.maxLength;
    this.autogrow();
    this.pulse();
    el.focus();
  }

  private pulse(): void {
    this.sendingPulse = true;
    if (this.pulseTimer) {
      clearTimeout(this.pulseTimer);
    }
    this.pulseTimer = setTimeout(() => {
      this.pulseTimer = null;
      this.sendingPulse = false;
      this.cdr.markForCheck();
    }, SEND_PULSE_MS);
  }

  /* ---- attachments ---- */

  onFiles(event: Event): void {
    const el = event.target as HTMLInputElement;
    const picked: File[] = el.files ? Array.prototype.slice.call(el.files) : [];
    el.value = '';
    if (picked.length === 0) {
      return;
    }
    picked.forEach(file => {
      const image = file.type.indexOf('image/') === 0;
      const entry: ChatComposerAttachment = { file, preview: '', image };
      this.attachments = [...this.attachments, entry];
      if (image) {
        const reader = new FileReader();
        reader.onload = () => {
          this.attachments = this.attachments.map(a => a === entry ? { ...a, preview: String(reader.result || '') } : a);
          this.cdr.markForCheck();
        };
        reader.readAsDataURL(file);
      }
    });
    this.input.nativeElement.focus();
  }

  removeAttachment(index: number): void {
    this.attachments = this.attachments.filter((_a, i) => i !== index);
    this.input.nativeElement.focus();
  }

  trackByAttachment = (i: number, a: ChatComposerAttachment) => a.file.name + ':' + a.file.size + ':' + i;

  /* ---- field ---- */

  onInput(): void {
    const value = this.input.nativeElement.value || '';
    this.detectMention(value);
    const match = /^\/(\S*)$/.exec(value);
    if (match) {
      this.quickManual = false;
      this.openQuick(match[1]);
    } else if (this.quickOpen && !this.quickManual) {
      this.closeQuick();
    }
    this.hasText = value.trim().length > 0;
    this.remaining = Math.max(0, this.maxLength - value.length);
    this.autogrow();
    if (this.hasText) {
      this.noteTyping();
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.mentionOpen) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.mentionIndex = this.mentionFiltered.length > 0 ? (this.mentionIndex + 1) % this.mentionFiltered.length : 0;
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.mentionIndex = this.mentionFiltered.length > 0 ? (this.mentionIndex - 1 + this.mentionFiltered.length) % this.mentionFiltered.length : 0;
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        const pick = this.mentionFiltered[this.mentionIndex];
        if (pick) {
          event.preventDefault();
          this.pickMention(pick);
          return;
        }
        this.closeMention();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.closeMention();
        return;
      }
    }
    if (this.quickOpen) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.quickIndex = this.quickFiltered.length > 0 ? (this.quickIndex + 1) % this.quickFiltered.length : 0;
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.quickIndex = this.quickFiltered.length > 0
          ? (this.quickIndex - 1 + this.quickFiltered.length) % this.quickFiltered.length
          : 0;
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        const pick = this.quickFiltered[this.quickIndex];
        if (pick) {
          event.preventDefault();
          this.pickQuick(pick);
          return;
        }
        // "/algo" sem correspondência: fecha a lista e deixa o Enter enviar.
        this.closeQuick();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.closeQuick();
        return;
      }
    }
    if (event.key === 'Escape' && this.emojiOpen) {
      event.preventDefault();
      this.closeEmoji();
      return;
    }
    if (event.key === 'Escape' && this.editing) {
      event.preventDefault();
      this.editCancel.emit();
      return;
    }
    if (event.key === 'Escape' && this.replyingTo) {
      event.preventDefault();
      this.replyCancel.emit();
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey && !event.isComposing) {
      event.preventDefault();
      this.submit();
    }
  }

  /** Grows with the content up to MAX_ROWS lines, then scrolls. */
  private autogrow(): void {
    const el = this.input.nativeElement;
    const max = LINE_HEIGHT * MAX_ROWS + FIELD_PADDING;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, max);
    el.style.height = next + 'px';
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
  }

  /* ---- emoji ---- */

  toggleActions(): void {
    this.actionsOpen = !this.actionsOpen;
    if (this.actionsOpen) {
      this.closeQuick();
      this.closeEmoji();
    }
  }

  pickAction(a: ChatComposerAction): void {
    this.actionsOpen = false;
    this.action.emit(a.key);
  }

  trackByAction = (_i: number, a: ChatComposerAction) => a.key;

  toggleEmoji(): void {
    this.emojiOpen = !this.emojiOpen;
    if (this.emojiOpen) {
      this.closeQuick();
    }
  }

  closeEmoji(): void {
    if (this.emojiOpen) {
      this.emojiOpen = false;
      this.cdr.markForCheck();
    }
  }

  /** Inserts at the caret (replacing a selection), keeps the picker open and the caret after the emoji. */
  insertEmoji(emoji: string): void {
    const el = this.input.nativeElement;
    const value = el.value || '';
    const start = typeof el.selectionStart === 'number' ? el.selectionStart : value.length;
    const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : start;
    if (value.length - (end - start) + emoji.length > this.maxLength) {
      return;
    }
    el.value = value.slice(0, start) + emoji + value.slice(end);
    const caret = start + emoji.length;
    el.focus();
    try {
      el.setSelectionRange(caret, caret);
    } catch (e) {
      /* alguns navegadores móveis não suportam seleção programática */
    }
    this.onInput();
  }

  /* ---- menções (7.1) ---- */

  /** `@` seguido de texto, no fim do que está escrito, numa nota: abre a lista. */
  private detectMention(value: string): void {
    if (!this.isNote || this.mentionCandidates.length === 0) {
      this.closeMention();
      return;
    }
    const match = /(^|\s)@([^\s@]*)$/.exec(value);
    if (!match) {
      this.closeMention();
      return;
    }
    this.mentionStart = value.length - match[2].length - 1;
    const needle = match[2].toLowerCase();
    this.mentionFiltered = this.mentionCandidates
      .filter(c => !!c.name && (!needle || String(c.name).toLowerCase().indexOf(needle) > -1))
      .slice(0, 6);
    this.mentionIndex = 0;
    this.mentionOpen = this.mentionFiltered.length > 0;
  }

  pickMention(candidate: ChatMention): void {
    const el = this.input.nativeElement;
    const value = el.value || '';
    if (this.mentionStart < 0 || !candidate.name) {
      this.closeMention();
      return;
    }
    el.value = value.slice(0, this.mentionStart) + '@' + candidate.name + ' ';
    if (!this.mentions.some(m => m.id === candidate.id)) {
      this.mentions = [...this.mentions, candidate];
    }
    this.closeMention();
    el.focus();
    this.hasText = el.value.trim().length > 0;
    this.remaining = Math.max(0, this.maxLength - el.value.length);
    this.autogrow();
  }

  private closeMention(): void {
    this.mentionOpen = false;
    this.mentionFiltered = [];
    this.mentionIndex = 0;
    this.mentionStart = -1;
  }

  trackByMention = (_i: number, m: ChatMention) => m.id;

  /* ---- quick replies ---- */

  toggleQuick(): void {
    if (this.quickOpen) {
      this.closeQuick();
      return;
    }
    this.closeEmoji();
    this.quickManual = true;
    this.openQuick('');
    this.cdr.markForCheck();
    this.input.nativeElement.focus();
  }

  pickQuick(reply: ChatQuickReply): void {
    const el = this.input.nativeElement;
    el.value = resolveTemplateVars(reply.message || '', this.templateVars);
    this.closeQuick();
    el.focus();
    // The cursor goes to the end so the user can keep writing.
    const end = el.value.length;
    try {
      el.setSelectionRange(end, end);
    } catch (e) {
      /* inputs de alguns tipos não suportam seleção */
    }
    this.hasText = el.value.trim().length > 0;
    this.remaining = Math.max(0, this.maxLength - el.value.length);
    this.autogrow();
    this.noteTyping();
  }

  trackByQuick = (_i: number, r: ChatQuickReply) => r.id || r.shortcut;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.actionsOpen && !this.host.nativeElement.contains(event.target as Node)) {
      this.actionsOpen = false;
      this.cdr.markForCheck();
    }
    if (this.quickOpen && !this.host.nativeElement.contains(event.target as Node)) {
      this.closeQuick();
    }
  }

  private openQuick(prefix: string): void {
    this.quickOpen = true;
    this.filterQuick(prefix);
  }

  private filterQuick(prefix: string): void {
    this.quickPrefix = prefix;
    const needle = ('/' + prefix).toLowerCase();
    const all = this.quickReplies || [];
    this.quickFiltered = prefix
      ? all.filter(r => String(r.shortcut || '').toLowerCase().indexOf(needle) === 0
        || String(r.message || '').toLowerCase().indexOf(prefix.toLowerCase()) > -1)
      : all.slice();
    this.quickIndex = 0;
  }

  private closeQuick(): void {
    this.quickOpen = false;
    this.quickManual = false;
    this.quickFiltered = [];
    this.quickIndex = 0;
  }

  /* ---- typing ---- */

  private noteTyping(): void {
    if (this.mode === 'note' || this.editing) {
      return;
    }
    if (!this.typingActive) {
      this.typingActive = true;
      this.typing.emit(true);
    }
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
    }
    this.typingTimer = setTimeout(() => {
      this.typingTimer = null;
      this.stopTyping(true);
    }, TYPING_IDLE_MS);
  }

  private stopTyping(emit: boolean): void {
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
      this.typingTimer = null;
    }
    if (this.typingActive) {
      this.typingActive = false;
      if (emit) {
        this.typing.emit(false);
      }
    }
  }
}
